/**
 * The damage pipeline — one path for every source of harm in the game
 * (AoD §4.3).
 *
 * ```
 * woundLevel = clamp( margin + weaponDR − targetTO − armourAV(part), 0, 5 )
 * ```
 *
 * A spear thrust, a wolf's jaws, a fall from a crag and a hand in a cooking
 * fire all arrive here with the same four numbers. Melee (M2-02) supplies the
 * margin from a contested roll; an environmental source supplies a severity
 * roll instead. There is no branch for either — that is the point. Every new
 * hazard the world grows is a set of numbers and an attack profile, not a
 * special case in the resolver, and every one of them inherits armour
 * coverage, hit location, wound tables and narration for free.
 *
 * Everything here is pure and rng-explicit: same stream position + same inputs
 * ⇒ same location, same level, same wound.
 */
import { strengthRating } from './body.js';
import { applyWound, createWound, MAX_WOUND_LEVEL, WoundTableError } from './wound.js';
/** A fist, near enough. What a weapon with nothing declared behaves like. */
export const DEFAULT_TIMING = {
    windUp: 3,
    recovery: 2,
    reach: 1,
    insidePenalty: 0,
    staminaCost: 1,
};
export function timingOf(weapon) {
    return weapon?.timing ?? DEFAULT_TIMING;
}
/**
 * How sharp a blade still is (M2-04's condition-lite).
 *
 * Three states rather than a durability float, for the reason the stamina bands
 * are three: the player has to be able to read their own kit off one word, and
 * a continuous edge value is a continuous balance argument nobody finishes.
 * Full durability — shafts breaking, mail needing a smith — is post-MVP.
 */
export const WEAPON_EDGES = [
    { id: 'keen', damage: 0 },
    { id: 'worn', damage: -1 },
    { id: 'blunted', damage: -2 },
];
export function edgeModifier(edge) {
    return WEAPON_EDGES.find((state) => state.id === edge)?.damage ?? 0;
}
/**
 * What one piece is worth against one kind of blow. Rounded down, so a
 * behaviour modifier can take a piece to nothing but never to less — armour
 * that made a wound *worse* would be a bug the wound tables could not express.
 */
export function effectiveArmourValue(piece, type) {
    const factor = type === undefined ? 1 : (piece.against?.[type] ?? 1);
    return Math.max(0, Math.floor(piece.armourValue * factor));
}
/**
 * Level-0 hits are narrated but not recorded. A bruise is worth a clause and
 * nothing else; keeping the record would fill a veteran's wound sheet with
 * scratches and make `effectivePain` iterate hundreds of zeroes.
 */
export const MIN_RECORDED_LEVEL = 1;
/**
 * Effective DR: `ST±n` for muscle weapons, flat for everything else, less
 * whatever the edge has lost since it was last on a whetstone.
 */
export function weaponDamageRating(weapon, attacker, edge) {
    const dulled = weapon.takesEdge ? edgeModifier(edge) : 0;
    if (!weapon.strengthScaled || !attacker)
        return weapon.damageRating + dulled;
    return strengthRating(attacker) + weapon.damageRating + dulled;
}
/**
 * The best armour covering a zone, *against this kind of blow*. Layering (a
 * jerkin under mail) is post-MVP: taking the maximum rather than the sum keeps
 * a character from stacking three cheap coats into plate.
 *
 * The maximum is taken over the **effective** values, not the raw ones, which
 * is what makes the type behaviours honest: a man in a mail byrnie over a
 * leather jerkin is better off in the leather when the axe-haft comes down, and
 * the pipeline should say so rather than picking the piece with the bigger
 * number on the label.
 */
export function armourValueAt(armour, zone, type) {
    if (!armour || armour.length === 0)
        return 0;
    let best = 0;
    for (const piece of armour) {
        if (!piece.covers.includes(zone))
            continue;
        const value = effectiveArmourValue(piece, type);
        if (value > best)
            best = value;
    }
    return best;
}
/** `clamp(margin + DR − TO − AV, 0, 5)`. The whole of AoD §4.3, in one line. */
export function woundLevelFor(input) {
    const raw = Math.round(input.margin + input.damageRating - input.toughness - input.armourValue);
    if (raw <= 0)
        return 0;
    if (raw >= MAX_WOUND_LEVEL)
        return MAX_WOUND_LEVEL;
    return raw;
}
/**
 * How many times an attacker must outmass a defender before a solid blow to its
 * vitals is simply fatal.
 *
 * The wound tables are written for people fighting people, where a broken rib is
 * survivable — correctly. But the same table applied to a rabbit turns a grown
 * man's fist into a bruise: a shattered chest is a `break`, never a `kill`, so a
 * 2 kg animal could soak seventy blows before a max-severity head blow happened
 * to roll (observed live, RC2). The physics the table omits is *scale*: a blow
 * that breaks a man's rib punctures a rabbit's lung and stops its heart. `frame`
 * (the M2 build attribute — rabbit 2, man ~10, cow 16) is the mass proxy already
 * in the model, and this ratio is the one number that reintroduces scale into
 * lethality without touching the human-vs-human numbers at all.
 *
 * 2.5 keeps every ordinary fight untouched — man vs man (1.0), man vs wolf
 * (~1.0), man vs boar (~0.7) — and fires only on a genuine mismatch: a person or
 * a wolf against a rabbit or a hare.
 */
export const MASS_KILL_RATIO = 2.5;
/**
 * The trauma a mass-kill needs: a real wound, not a graze. Level 3 is where the
 * blunt ladder starts breaking bone and the cut/puncture ladders open the body
 * — the rung at which "and it is much smaller than you" becomes fatal.
 */
export const MASS_KILL_MIN_LEVEL = 3;
/** Maximum wound-level shift caused by a mismatch in the bodies' scale. */
export const FRAME_WOUND_MODIFIER_LIMIT = 2;
/**
 * Translate a real mismatch in frame into wound-table depth.
 *
 * The tables are human-calibrated. Each doubling in the attacker's frame over
 * the defender's moves the result one rung deeper; each halving moves it one
 * rung shallower. `Math.trunc` deliberately leaves ordinary differences alone
 * (a frame-8 person against a frame-12 person is still the same scale), while a
 * frame-10 hunter against a frame-2 rabbit gains two levels. Environmental
 * harm has no attacker frame and therefore receives no adjustment.
 */
export function frameWoundModifier(attackerFrame, defenderFrame) {
    if (attackerFrame === undefined ||
        defenderFrame === undefined ||
        !Number.isFinite(attackerFrame) ||
        !Number.isFinite(defenderFrame) ||
        attackerFrame <= 0 ||
        defenderFrame <= 0) {
        return 0;
    }
    const modifier = Math.trunc(Math.log2(attackerFrame / defenderFrame));
    return Math.max(-FRAME_WOUND_MODIFIER_LIMIT, Math.min(FRAME_WOUND_MODIFIER_LIMIT, modifier));
}
/**
 * Is this blow fatal by sheer disparity of size?
 *
 * True only when all of: there is an attacker (frames known and the defender has
 * mass), the part struck is *vital*, the wound is at least `MASS_KILL_MIN_LEVEL`,
 * and the attacker outmasses the defender by `MASS_KILL_RATIO`. A miss on any
 * one leaves the table's own verdict standing.
 */
export function isMassKill(attackerFrame, defenderFrame, level, vital) {
    if (attackerFrame === undefined || defenderFrame === undefined || defenderFrame <= 0)
        return false;
    if (!vital || level < MASS_KILL_MIN_LEVEL)
        return false;
    return attackerFrame >= defenderFrame * MASS_KILL_RATIO;
}
/** The parts of a plan that live in a zone. */
export function partsInZone(plan, zone) {
    return plan.parts.filter((part) => part.zone === zone);
}
/** Every zone a plan actually has. The domain a zone roll may land in. */
export function zonesOf(plan) {
    return [...new Set(plan.parts.map((part) => part.zone))];
}
/**
 * Step one of AoD §3.1: which zone. Weights come from the attack profile,
 * restricted to zones this body has.
 */
export function rollZone(rng, plan, profile, targetingSkill = 0) {
    const candidates = zonesOf(plan).filter((zone) => (profile.zoneWeights[zone] ?? 0) > 0);
    if (candidates.length === 0) {
        throw new WoundTableError(`attack profile '${profile.id}' can reach no zone of body plan '${plan.id}'`);
    }
    const skill = Number.isFinite(targetingSkill) ? Math.max(0, targetingSkill) : 0;
    const declaredBias = profile.vitalZoneBiasPerSkill ?? 0;
    const bias = Number.isFinite(declaredBias) ? Math.max(0, declaredBias) : 0;
    return rng.pickWeighted(candidates, (zone) => {
        const base = profile.zoneWeights[zone] ?? 0;
        const vital = plan.parts.some((part) => part.zone === zone && part.vital === true);
        return vital ? base * (1 + skill * bias) : base;
    });
}
/** Step two: which part within the zone, by `hitWeight`. */
export function rollPartInZone(rng, plan, zone) {
    const candidates = partsInZone(plan, zone);
    if (candidates.length === 0) {
        throw new WoundTableError(`body plan '${plan.id}' has no part in zone '${zone}'`);
    }
    return rng.pickWeighted(candidates, (part) => part.hitWeight);
}
/**
 * The two-step location roll, or the aimed part if one was named.
 *
 * An aimed blow (M2-03) costs to-hit accuracy, not dice here: the penalty is
 * applied by the attack resolution before the margin reaches this module. By
 * the time we know a blow landed, "where" is already decided.
 */
export function rollHitLocation(rng, plan, profile, targetPart, targetingSkill = 0) {
    if (targetPart !== undefined) {
        const aimed = plan.parts.find((part) => part.id === targetPart);
        if (!aimed)
            throw new WoundTableError(`body plan '${plan.id}' has no part '${targetPart}'`);
        return aimed;
    }
    return rollPartInZone(rng, plan, rollZone(rng, plan, profile, targetingSkill));
}
/**
 * Resolve one hit, end to end: where it landed, how bad it was, what the table
 * says that means, and what it did to the body.
 *
 * The order of rolls is fixed and load-bearing — zone, then part — because it
 * is what a replay reproduces. Adding a roll before them would shift every
 * subsequent hit in every archived battle.
 */
export function resolveDamage(input) {
    const part = rollHitLocation(input.rng, input.plan, input.profile, input.targetPart, input.targetingSkill);
    const armourValue = armourValueAt(input.armour, part.zone, input.profile.damageType);
    const margin = input.margin +
        (input.profile.damageBonus ?? 0) +
        frameWoundModifier(input.attackerFrame, input.defenderFrame);
    const level = woundLevelFor({
        margin,
        damageRating: input.damageRating,
        toughness: input.toughness,
        armourValue,
    });
    const entry = input.table.resolve(input.profile.damageType, part.zone, level);
    const unarmoured = woundLevelFor({
        margin,
        damageRating: input.damageRating,
        toughness: input.toughness,
        armourValue: 0,
    });
    if (level < MIN_RECORDED_LEVEL) {
        return {
            part,
            zone: part.zone,
            level,
            entry,
            armourValue,
            turnedByArmour: armourValue > 0 && unarmoured > level,
            application: null,
        };
    }
    // Scale has already moved the wound to the appropriate table rung. A solid
    // blow to the vitals of a body the attacker vastly outmasses is fatal where
    // the human-calibrated entry would still leave it standing. Carried as a
    // bonus `kill` effect so death keeps its single door (`applyWound`).
    const massKill = isMassKill(input.attackerFrame, input.defenderFrame, level, part.vital ?? false);
    const wound = createWound({
        id: input.woundId,
        part: part.id,
        zone: part.zone,
        type: input.profile.damageType,
        level,
        entry,
        inflictedAt: input.tick,
        provenance: input.provenance,
        ...(massKill ? { bonusEffects: ['kill'] } : {}),
    });
    return {
        part,
        zone: part.zone,
        level,
        entry,
        armourValue,
        turnedByArmour: armourValue > 0 && unarmoured > level,
        application: applyWound(input.body, input.wounds, wound),
    };
}
/**
 * Every `(type, zone)` a set of profiles can reach on a set of plans — the
 * input to the wound-table completeness test (M1-02).
 *
 * Computed from the content rather than declared by it, so adding a creature
 * with a tail, or a profile that can hit an eye, makes the completeness test
 * fail until somebody writes the entries. A table is complete relative to what
 * the world can actually do to a body, not to a list someone kept up to date.
 */
export function reachableCells(plans, profiles) {
    const cells = new Map();
    for (const profile of profiles) {
        for (const plan of plans) {
            for (const zone of zonesOf(plan)) {
                if ((profile.zoneWeights[zone] ?? 0) <= 0)
                    continue;
                cells.set(`${profile.damageType}/${zone}`, { type: profile.damageType, zone });
            }
        }
    }
    return [...cells.values()];
}
/**
 * Assert a table covers everything the profiles can reach, and that every
 * fatal location is actually flagged fatal at level 5.
 *
 * Lives here rather than in the test file so `@annwn/content` can run it at
 * load time in a headless harness, where a silently incomplete table would
 * otherwise surface as a thrown error 9,000 duels into a 10,000-duel run.
 */
export function assertTableCovers(table, plans, profiles) {
    const missing = reachableCells(plans, profiles).filter((cell) => !table.has(cell.type, cell.zone));
    if (missing.length > 0) {
        const list = missing.map((cell) => `${cell.type}/${cell.zone}`).join(', ');
        throw new WoundTableError(`is missing reachable cells: ${list}`);
    }
    const vitalZones = new Set(plans.flatMap((plan) => plan.parts.filter((part) => part.vital).map((part) => part.zone)));
    for (const cell of table.coverage) {
        if (!vitalZones.has(cell.zone))
            continue;
        const worst = table.resolve(cell.type, cell.zone, MAX_WOUND_LEVEL);
        if (!worst.effects.includes('kill')) {
            throw new WoundTableError(`${cell.type} to the ${cell.zone} at level ${MAX_WOUND_LEVEL} is not fatal, but the ${cell.zone} is vital`);
        }
    }
}
//# sourceMappingURL=damage.js.map